import React, { useEffect, useState, useRef } from "react";
import { FiChevronDown } from "react-icons/fi";
import useSound from "use-sound";
import toast from "react-hot-toast";

import Header from "./Header";
import useSize from "../../../../Utils/useSize/useSize";
import NotFound from "../../../../Utils/NotFound/NotFound";
import ErrorSound from "../../../../Sound/Error.wav";
import BuySound from "../../../../Sound/buy.mp3";
import SellSound from "../../../../Sound/sell.mp3";
import Bid from "./BidB";
import Ask from "./AskB";
import Backend from "../../../../Backend/Backend";
import SqButton from "./SqButton";
import SymbolQty from "./SymbolQty";
import HighLow from "./HighLow";

export default function BuySell() {

  const windowSize = useSize();
  const dropdownRef = useRef(null);

  const [backend, setBackend] = useState(null);
  const [errorsound] = useSound(ErrorSound, { volume: 0.5 });
  const [buysound] = useSound(BuySound, { volume: 0.5 });
  const [sellsound] = useSound(SellSound, { volume: 0.5 });

  const [symbol, setSymbol] = useState(null);
  const [qty, setQty] = useState("");
  const [orderType, setOrderType] = useState("Market Execution");
  const [openType, setOpenType] = useState(false);
  const [stopLoss, setStopLoss] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [loading, setLoading] = useState(false);
  const [symbolQty, setSymbolQty] = useState("");

  // ✅ trigger to refresh SqButton
  const [refreshSq, setRefreshSq] = useState(0);

  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("info");

  const showAlert = (msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(null), 3000);
  };

  useEffect(() => {
    (async () => {
      const b = await new Backend().init();
      setBackend(b);
    })();
  }, []);

  useEffect(() => {
    const savedSymbol =
      window.symboldata ||
      JSON.parse(localStorage.getItem("symboldata") || "null");
    if (savedSymbol) {
      setSymbol(savedSymbol);
      localStorage.setItem("symboldata", JSON.stringify(savedSymbol));
    }
  }, []);

  useEffect(() => {
    if (symbolQty?.min_qty) {
      let qty =
        symbolQty.type === "nse" ||
          symbolQty.type === "nse_cash" ||
          symbolQty.type === "us_stock" ||
          symbolQty.type === "sgx"
          ? symbolQty.min_qty
          : symbolQty.min_qty / symbolQty.lot_size;

      setQty(qty);
    }
  }, [symbolQty]);

  useEffect(() => {
    if (!backend || !symbol?.symbol_id) return;
    const fetchSymbolQty = async () => {
      try {
        setLoading(true);
        const data = {
          server: localStorage.getItem("server"),
          id: localStorage.getItem("id"),
          username: localStorage.getItem("username"),
          token: localStorage.getItem("token"),
          symbol_id: symbol.symbol_id,
        };
        const r = await backend.symbol_qty(data);
        setLoading(false);

        if (r.error === "False") {
          setSymbolQty(r.symbol);
        } else {
          toast.error(r.message);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load symbol details");
        setLoading(false);
      }
    };
    fetchSymbolQty();
  }, [backend, symbol]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenType(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!symbol)
    return (
      <div className="flex justify-center items-center text-gray-400">
        <NotFound
          small
          height={windowSize[1] - 100}
          title="Nothing here"
          subtext="Loading symbol..."
        />
      </div>
    );

  // ✅ Submit order
  const handleSubmit = async (side) => {
    const soundEnabled = JSON.parse(localStorage.getItem("sound"));

    if (!backend)
      return (
        showAlert("Backend not initialized", "error"),
        soundEnabled && errorsound()
      );
    if (!symbol)
      return (
        showAlert("Select a symbol first", "error"),
        soundEnabled && errorsound()
      );
    if (!qty || qty <= 0)
      return (
        showAlert("Enter a valid Quantity", "error"),
        soundEnabled && errorsound()
      );

    if (orderType === "Take Profit" && (!takeProfit || Number(takeProfit) <= 0))
      return (
        showAlert("Enter a valid Take Profit", "error"),
        soundEnabled && errorsound()
      );

    if (orderType === "Stop Loss" && (!stopLoss || Number(stopLoss) <= 0))
      return (
        showAlert("Enter a valid Stop Loss", "error"),
        soundEnabled && errorsound()
      );

    setLoading(true);
    try {
      const orderData = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        user_id: localStorage.getItem("id"),
        qty_order: qty,
        symbol_id: symbol.symbol_id,
        symbol: symbol.symbol_display ?? symbol.symbol,
        symbol_type: symbol.type,
        type: side,
        buy_sell:
          orderType === "Market Execution"
            ? 0
            : orderType === "Take Profit"
              ? 1
              : 2,
        price:
          orderType === "Market Execution"
            ? null
            : orderType === "Take Profit"
              ? takeProfit
              : stopLoss,
      };

      const r = await backend.buy_sell(orderData);
      setLoading(false);

      if (r.error === "False") {
        showAlert(r.message, "success");

        // 🎧 Play sound only when sound
        if (soundEnabled) {
          side === 1 ? buysound() : sellsound();
        }

        setQty("");
        setStopLoss("");
        setTakeProfit("");
        setRefreshSq((prev) => prev + 1);
      } else {
        showAlert(r.message, "error");
        if (soundEnabled) errorsound();
      }
    } catch (err) {
      console.error(err);
      showAlert("Order failed. Try again.", "error");
      if (localStorage.getItem("sound")) errorsound();
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        height: windowSize[1] - 100,
      }}
      className="bg-[var(--background)] flex flex-col relative"
    >
      <Header symbol={symbol} updateSymbol={setSymbol} />

      <HighLow symbol_id={symbol.symbol_id} />

      <SymbolQty symbolQty={symbolQty} />

      {/* ⚠️ Alert Modal */}
      {message && (
        <div
          className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
          z-50 w-[90%] max-w-md p-6 rounded-2xl shadow-2xl border-2 text-center
          ${messageType === "success"
              ? "bg-green-50 border-green-400 text-green-800"
              : "bg-red-50 border-red-400 text-red-800"
            }`}
        >
          <h2 className="text-lg font-bold mb-2">
            {messageType === "success" ? "Success!" : "Alert!"}
          </h2>
          <p className="text-sm mb-5">{message}</p>
          <button
            onClick={() => setMessage(null)}
            className={`px-5 py-2 rounded-lg font-semibold ${messageType === "success"
              ? "bg-green-600 text-white"
              : "bg-red-600 text-white"
              }`}
          >
            OK
          </button>
        </div>
      )}

      <div
        style={{
          paddingBottom: 400,
          overflowY: "scroll",
        }}
        className="flex-1 px-4 py-4 pt-0 bg-[var(--background)] space-y-5 mt-2 relative"
      >
        {/* 🔽 Order Type */}
        <div ref={dropdownRef}>
          <p className="text-[var(--text)] mb-1 text-sm font-medium">
            Order Type
          </p>
          <div
            onClick={() => setOpenType(!openType)}
            className="flex justify-between items-center border-2 border-[var(--hover)] rounded-md px-3 py-2 cursor-pointer bg-[var(--background)]"
          >
            <span className="text-[var(--text)] text-sm">{orderType}</span>
            <FiChevronDown
              className={`text-[var(--text)] transition-transform duration-200 ${openType ? "rotate-180" : ""
                }`}
            />
          </div>
          {openType && (
            <div className="absolute w-[calc(100%-2rem)] rounded-md mt-1 bg-[var(--background)] text-[var(--text)] border-2 border-[var(--hover)] shadow-md z-10">
              {["Market Execution", "Take Profit", "Stop Loss"].map((opt) => (
                <div
                  key={opt}
                  onClick={() => {
                    setOrderType(opt);
                    setOpenType(false);
                    setStopLoss("");
                    setTakeProfit("");
                  }}
                  className={`px-3 py-2 cursor-pointer text-sm ${orderType === opt
                    ? "bg-[var(--buymeta)] text-[var(--white)] font-medium"
                    : ""
                    }`}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 📦 Quantity */}
        <div>
          <label className="text-[var(--text)] block mb-1">
            {symbol.type === "nse" || symbol.type === "nse_cash" || symbol.type === "us_stock" || symbol.type === "sgx"
              ? "Quantity"
              : "Lot"}
          </label>
          <input
            type="number"
            placeholder="0.00"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="w-full border-2 rounded-md px-3 py-2 text-[var(--text)] bg-[var(--background)] border-[var(--hover)] placeholder-[var(--text)] focus:outline-none"
          />
        </div>

        {/* ⛔ Stop Loss / Take Profit */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <label className="text-[var(--text)] block mb-1">Stop Loss</label>
            <input
              type="number"
              placeholder="0.00"
              value={stopLoss}
              onChange={(e) => setStopLoss(e.target.value)}
              disabled={orderType !== "Stop Loss"}
              className={`w-full border-2 border-[var(--hover)] bg-[var(--background)] text-[var(--text)] rounded-md px-3 py-2 ${orderType === "Stop Loss"
                ? "border-[var(--sellmeta)] "
                : "text-[var(--text)] cursor-not-allowed"
                }`}
            />
          </div>

          <div>
            <label className="text-[var(--text)] block mb-1">Take Profit</label>
            <input
              type="number"
              placeholder="0.00"
              value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)}
              disabled={orderType !== "Take Profit"}
              className={`w-full border-2 border-[var(--hover)] text-[var(--text)] bg-[var(--background)] rounded-md px-3 py-2 ${orderType === "Take Profit"
                ? "border-[var(--primary)]"
                : "text-[var(--text)] cursor-not-allowed"
                }`}
            />
          </div>
        </div>

        {/* 💰 Prices */}
        {/* <div className="flex justify-between items-center mt-6 mb-2">
          <Bid symbol_id={symbol.symbol_id} />
          <Ask symbol_id={symbol.symbol_id} />
        </div> */}
        {/* {usdInr !== null ? (
          <div className="flex justify-between items-center mt-6 mb-2">
            <Bid symbol_id={usdInr.symbol_id} usd={1} />
            <Ask symbol_id={usdInr.symbol_id} usd={1} />
          </div>
        ) : null} */}

        {/* 🟢 Buy/Sell */}
        <div className="grid grid-cols-2 rounded-md overflow-hidden">
          <button
            disabled={loading}
            onClick={() => handleSubmit(1)}
            className="bg-[var(--sellmeta)] text-white py-1 font-semibold text-sm active:scale-95 transition disabled:opacity-50"
          >
            <div>Sell by Market</div>
            <div>
              <Bid symbol_id={symbol.symbol_id} />
            </div>
          </button>
          <button
            disabled={loading}
            onClick={() => handleSubmit(0)}
            className="bg-[var(--buymeta)] text-white py-1 font-semibold text-sm active:scale-95 transition disabled:opacity-50"
          >
            <div>Buy by Market</div>
            <div>
              <Ask symbol_id={symbol.symbol_id} />
            </div>
          </button>
        </div>

        {/* 🔁 SqButton auto refresh */}
        <SqButton key={refreshSq} symbol_id={symbol.symbol_id} />

        <p className="text-[11px] text-center text-gray-500 mt-4">
          Attention! Trade will execute at market price. Price difference may
          occur.
        </p>
      </div>
    </div>
  );
}
